<template>
  <div class="settings-form-groups-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Form Groups</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        @click="addFormGroup"
        variant="primary"
        >New</b-button
      >
    </div>
    <grid
      class=""
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      centered
      ref="form-modal"
      :title="formId ? 'Edit Form Group' : 'Add Form Group'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitFormGroup"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <div class="container text-right p-0" v-if="formId">
        <b-dropdown variant="primary" right size="sm">
          <template #button-content>
            <i class="fa fa-clock mr-2" style="width: 16px"></i>History
          </template>
          <template v-for="(version, index) in versions">
            <b-dropdown-item-button
              :key="index"
              @click="setVersionAndSubmit(version)"
              :disabled="versionDisabled(version)"
            >
              <div class="d-flex justify-content-between">
                <div>{{ index + 1 }}. {{ version.text }}</div>
                <div class="ml-1">
                  <b-badge :variant="versionVariant(version)">{{
                    versionText(version)
                  }}</b-badge>
                </div>
              </div>
            </b-dropdown-item-button>
          </template>
        </b-dropdown>
      </div>
      <b-form-group
        label="Name"
        :state="formErrors.name.length == 0 ? null : false"
        :invalid-feedback="formErrors.name.join(', ')"
      >
        <b-form-input
          placeholder=""
          v-model="form.name"
          trim
          :state="formErrors.name.length == 0 ? null : false"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Menu">
        <b-form-select v-model="form.menuId" :options="menus"></b-form-select>
      </b-form-group>
      <b-form-group label="Forms">
        <multiple-select
          :options="formOptions"
          v-model="form.formIds"
          :listDraggable="true"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import formGroupServer from "../../server/formGroup";
import { mapState, mapActions, mapMutations } from "vuex";
import { validateRequired } from "../../helpers/validation";
import MultipleSelect from "../controls/MultipleSelect.vue";
import { formatDateOnly } from "../../helpers/date";

export default {
  name: "FormGroupCrud",
  components: {
    Grid,
    MultipleSelect
  },
  data() {
    return {
      formId: null,
      form: {
        name: "",
        menuId: null,
        selectedFormId: null,
        formIds: []
      },
      name: "",
      menuId: null,
      formIds: [],
      versions: [],
      current: null,
      formErrors: {
        name: []
      }
    };
  },
  async mounted() {
    this.getFormGroups();
    this.fetchFormBuilderForms({ category: "main" });
    this.fetchFormBuilderMenus();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("formBuilder", [
      "fetchFormBuilderMenus",
      "fetchFormBuilderForms"
    ]),
    ...mapActions("formGroup", ["fetchFormGroups"]),
    ...mapMutations("client", ["dispatchListener"]),
    async getFormGroups() {
      this.fetchFormGroups();
    },
    async submitFormGroup(modalEvent) {
      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        modalEvent?.preventDefault();
      } else {
        const formData = {
          ...this.form,
          formIds: this.form.formIds.map(f => f.value)
        };
        if (this.formId) {
          await formGroupServer.update(this.formId, formData);
        } else {
          await formGroupServer.create(formData);
        }
        this.getFormGroups();
        this.dispatchListener({
          listener: "fetchDocumentMenus",
          param: "all"
        });
        if (this.formId) this.fetchData();
        this.showAlertSuccess();
      }
    },
    addFormGroup() {
      this.formId = null;
      const defaultMenu = this.menus.find(m => !m.data.parentId);
      this.form = {
        name: "",
        menuId: defaultMenu ? defaultMenu.value : null,
        selectedFormId: null,
        formIds: []
      };
      this.$refs["form-modal"].show();
    },
    async setCurrentVersion() {
      const formGroup = await formGroupServer.setCurrentVersion(
        this.formId,
        this.current
      );
      this.form.formIds = formGroup.formIds.map(fId => {
        const form = this.formOptions.find(f => f.value == fId);
        return {
          value: fId,
          label: form ? form.label : "",
          data: form
        };
      });
      this.formIds = [...this.form.formIds];
      this.current = formGroup.current;
      this.showAlertSuccess();
    },
    setVersionAndSubmit(version) {
      this.current = version.value;
      if (this.formIdsChanged) {
        this.$dialog
          .confirm(
            {
              title: "Do you want to save current version."
            },
            {
              view: "multiple-actions",
              options: [{ value: true, label: "Continue" }]
            }
          )
          .then(async () => {
            await this.submitFormGroup();
            this.setCurrentVersion();
          })
          .catch(() => {
            this.setCurrentVersion();
          });
      } else {
        this.setCurrentVersion();
      }
    },
    async fetchData() {
      const form = await formGroupServer.get(this.formId);
      this.form = {
        name: form.name,
        menuId: form.menuId,
        formIds:
          form.formIds?.length > 0
            ? form.formIds.map(fId => {
                const form = this.formOptions.find(f => f.value == fId);
                return {
                  value: fId,
                  label: form ? form.label : "",
                  data: form
                };
              })
            : []
      };
      this.current = form.current;
      this.versions = form.versions.map(v => ({
        value: v.id,
        text: `${formatDateOnly(v.createdAt)} - ${v.createdBy?.fullName ?? ""}`,
        data: v
      }));
      this.formIds = [...this.form.formIds];
    },
    versionVariant(version) {
      return version.value == this.current ? "secondary" : "primary";
    },
    versionText(version) {
      return version.value == this.current ? "Current" : "Restore";
    },
    versionDisabled(version) {
      return version.value == this.current;
    }
  },
  computed: {
    ...mapState("formGroup", {
      list: state => state.list
    }),
    ...mapState("formBuilder", {
      formOptions: state =>
        state.list["main"]
          .map(f => ({
            value: f.id,
            label: f.name,
            data: f
          }))
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          ),
      menus: state =>
        state.menus.map(m => ({
          value: m.id,
          text: m.name,
          data: m
        }))
    }),
    items() {
      return this.list.map(item => ({
        ...item
      }));
    },
    columns() {
      let columns = [
        {
          text: "Name",
          field: "name",
          flex: 1,
          editor: false,
          sortable(a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          }
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 110,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formId = record.id;
                this.fetchData();
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await formGroupServer.delete(record.id);
                    this.getFormGroups();
                    this.showAlertSuccess();
                    this.dispatchListener({
                      listener: "fetchDocumentMenus",
                      param: "all"
                    });
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    },
    formIdsChanged() {
      return JSON.stringify(this.formIds) != JSON.stringify(this.form.formIds);
    }
  }
};
</script>

<style scoped>
.form-item:hover {
  cursor: pointer;
}
.forms-list {
  height: 300px;
  overflow-y: auto;
}
::v-deep .dropdown-menu {
  overflow: auto;
  max-height: 250px;
}
</style>

<style>
.settings-form-groups-container .b-grid-container .b-container {
  max-height: calc(100vh - 281px) !important;
}
</style>
